import moment from 'moment';

export function ProductCommissionPolicyArchiveService() {
    'ngInject';
    const service = {
        filterArchive: filterArchive,
        addArchive: addArchive,
        deleteArchive: deleteArchive,
        updateProductCommissionPolicyArchive: updateProductCommissionPolicyArchive,
        archiveDatesOverlap: archiveDatesOverlap,
        isValidPolicyPattern: isValidPolicyPattern
    };


    // eslint-disable-next-line @typescript-eslint/naming-convention
    const RuleType = {
        // A type of rule where installed dates are specified and order dates can never be specified.
        Install: 'Install',
        // A type of rule where order dates are specified and install dates can never be specified.
        Order: 'Order',
        // A type of rule where both install and order dates must be specified.
        OrderInstall: 'OrderInstall',
        // A type of rule where neither install nor order dates are specified and only one policy
        // can ever be specified.
        Singleton: 'Singleton'
    };

    return service;

    function filterArchive(archive) {
        return !archive.IsDeleted;
    }

    function addArchive(productCommissionPolicy) {
        const archive = {
            ProductCommisionPolicyID: 0,
            IsNew: true,
            IsDeleted: false,
            ActiveOrderStart: null,
            ActiveOrderEnd: null,
            ActiveInstalledStart: null,
            ActiveInstalledEnd: null,
            ProductID: productCommissionPolicy.ProductID,
            ProductLineID: productCommissionPolicy.ProductID,
            Amount: 0
        };
        productCommissionPolicy.Archives.unshift(archive);
        return archive;
    }

    function deleteArchive(archive, productCommissionPolicy, confirmationFn) {
        confirmationFn = confirmationFn || (() => true);
        if (archive.Locked) {
            throw "Cannot delete this archive";
        }

        if (archive.IsDeleted || !archive.IsNew && !confirmationFn()) {
            archive.IsDeleted = false;
            return;
        }

        archive.IsDeleted = true;
    }

    function updateProductCommissionPolicyArchive(productCommissionPolicy, archive) {
        const affectedArchives = [];
        const archives = productCommissionPolicy.Archives.filter(function (archive) {
            return !archive.IsDeleted;
        });

        const nextArchive = archives.reduce(function (nextArchive, other) {
            if (other === archive || moment(other.ActiveOrderStart).isSameOrBefore(archive.ActiveOrderStart)
                || moment(other.ActiveInstalledStart).isSameOrBefore(archive.ActiveInstalledStart)) {
                return nextArchive
            }

            if (!nextArchive || moment(nextArchive.ActiveOrderStart).isSameOrAfter(other.ActiveOrderStart)
                || moment(nextArchive.ActiveInstalledStart).isSameOrAfter(other.ActiveInstalledStart)) {
                return other;
            } else {
                return nextArchive;
            }
        }, null);

        if (nextArchive) {
            let isAffected = false;
            if (!archive.ActiveOrderEnd && nextArchive.ActiveOrderStart) {
                archive.ActiveOrderEnd = moment(nextArchive.ActiveOrderStart).subtract(1, "day").toDate();
                isAffected = true;
            }
            if (!archive.ActiveInstalledEnd && nextArchive.ActiveInstalledStart) {
                archive.ActiveInstalledEnd = moment(nextArchive.ActiveInstalledStart).subtract(1, "day").toDate();
                isAffected = true;
            }
            if (isAffected) {
                affectedArchives.push(archive);
            }
        }

        const previousArchive = archives.reduce(function (previousArchive, other) {
            if (other === archive || moment(other.ActiveOrderStart).isSameOrAfter(archive.ActiveOrderStart)
                || moment(other.ActiveInstalledStart).isSameOrAfter(archive.ActiveInstalledStart)) {
                return previousArchive
            }

            if (!previousArchive || moment(previousArchive.ActiveOrderStart).isSameOrBefore(other.ActiveOrderStart)
                || moment(previousArchive.ActiveInstalledStart).isSameOrBefore(other.ActiveInstalledStart)) {
                return other;
            } else {
                return previousArchive;
            }
        }, null);

        if (previousArchive) {
            let isAffectedByPrevious = false;
            if (!previousArchive.ActiveOrderEnd && archive.ActiveOrderStart) {
                previousArchive.ActiveOrderEnd = moment(archive.ActiveOrderStart).subtract(1, "day").toDate();
                isAffectedByPrevious = true;
            }
            if (!previousArchive.ActiveInstalledEnd && archive.ActiveInstalledStart) {
                previousArchive.ActiveInstalledEnd = moment(archive.ActiveInstalledStart).subtract(1, "day").toDate();
                isAffectedByPrevious = true;
            }
            if (isAffectedByPrevious) {
                affectedArchives.push(previousArchive);
            }
        }

        return affectedArchives;
    }

    function archiveDatesOverlap(archive, activeInstalledStart, activeInstalledEnd,
        activeOrderStart, activeOrderEnd, commissionPolicy) {

        return commissionPolicy.Archives.reduce(function (any, other) {
            const rule = determineRule(archive);
            let dateExpression;

            switch (rule) {
                case RuleType.Install:
                    dateExpression = intervalsOverlap(activeInstalledStart, activeInstalledEnd,
                        other.ActiveInstalledStart, other.ActiveInstalledEnd);
                    break;
                case RuleType.Order:
                    dateExpression = intervalsOverlap(activeOrderStart, activeOrderEnd,
                        other.ActiveOrderStart, other.ActiveOrderEnd);
                    break;
                case RuleType.OrderInstall:
                    dateExpression = intervalsOverlap(activeInstalledStart, activeInstalledEnd,
                        other.ActiveInstalledStart, other.ActiveInstalledEnd)
                        || intervalsOverlap(activeOrderStart, activeOrderEnd,
                            other.ActiveOrderStart, other.ActiveOrderEnd);
                    break;
                default:
                    break
            }

            return any || other !== archive // Archives can't conflict with themselves, of course.
                && !other.IsDeleted && dateExpression
        }, false);
    }

    function isValidPolicyPattern(commissionPolicy) {
        const rules = [];
        commissionPolicy.Archives.forEach(function (archive) {
            if (!archive.IsDeleted) {
                archive.Rule = determineRule(archive);
                if (!rules.includes(archive.Rule)) {
                    rules.push(archive.Rule);
                }
            }
        });
        return rules.length === 1;
    }

    function determineRule(policy) {
        let rule = RuleType.Singleton;
        if (policy.ActiveOrderStart || policy.ActiveOrderEnd) {
            rule = RuleType.Order;
        }
        if (policy.ActiveInstalledStart || policy.ActiveInstalledEnd) {
            if (rule === RuleType.Order) {
                rule = RuleType.OrderInstall;
            } else {
                rule = RuleType.Install;
            }
        }
    }

    function intervalsOverlap(startA, endA, startB, endB) {
        return (startA === null || endB === null ||
            startA <= endB) && (endA === null || startB === null || endA >= startB);
    }
}
